import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, Trophy } from 'lucide-react';
import { competitionScheduleService, CompetitionSchedule } from '../services/api';

const CompetitionSchedulePage = () => {
    const { t, i18n } = useTranslation();
    const [schedules, setSchedules] = useState<CompetitionSchedule[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSchedules();
    }, []);

    const fetchSchedules = async () => {
        try {
            const response = await competitionScheduleService.getSchedules();
            setSchedules(response.data.data);
        } catch (error) {
            console.error('Failed to fetch schedules:', error);
        } finally {
            setLoading(false);
        }
    };



    const groupSchedulesByDate = (schedules: CompetitionSchedule[]) => {
        const grouped = schedules.reduce((acc, schedule) => {
            const date = new Date(schedule.date);
            const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD for sorting
            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(schedule);
            return acc;
        }, {} as Record<string, CompetitionSchedule[]>);

        // Sort keys (dates) and then sort schedules within each date by time
        return Object.entries(grouped)
            .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
            .map(([dateKey, events]) => ({
                date: new Date(dateKey),
                events: events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            }));
    };

    const groupedSchedules = groupSchedulesByDate(schedules);

    const formatDateHeader = (date: Date) => {
        return date.toLocaleDateString(i18n.language, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString(i18n.language, {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center p-3 bg-yellow-100 rounded-full mb-4">
                        <Trophy className="w-8 h-8 text-yellow-600" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('competitionSchedule.title')}</h1>
                    <p className="text-lg text-gray-600">{t('competitionSchedule.subtitle')}</p>
                </div>

                {loading ? (
                    <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
                ) : schedules.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <p className="text-xl mb-2">{t('competitionSchedule.noEvents')}</p>
                        <p>{t('competitionSchedule.checkBack')}</p>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {groupedSchedules.map(({ date, events }) => (
                            <div key={date.toISOString()}>
                                <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2 flex items-center gap-2">
                                    <Calendar className="w-6 h-6 text-blue-600" />
                                    {formatDateHeader(date)}
                                </h2>
                                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                                    {events.map((schedule) => (
                                        <div
                                            key={schedule.id}
                                            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
                                        >
                                            {schedule.image ? (
                                                <div className="h-48 overflow-hidden">
                                                    <img
                                                        src={schedule.image}
                                                        alt={schedule.title}
                                                        className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                                    <Trophy className="w-16 h-16 text-white opacity-50" />
                                                </div>
                                            )}
                                            <div className="p-6 flex-grow flex flex-col">
                                                <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                                                    {schedule.title}
                                                </h3>
                                                <div className="space-y-3 mb-4 text-sm text-gray-600">
                                                    <div className="flex items-start gap-2 text-blue-600 font-semibold">
                                                        <Calendar className="w-4 h-4 mt-1 flex-shrink-0" />
                                                        <span>{formatTime(schedule.date.toString())}</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                                                        <span className="line-clamp-2">{schedule.location}</span>
                                                    </div>
                                                </div>
                                                <p className="text-gray-600 text-sm line-clamp-3 mb-4 flex-grow">
                                                    {schedule.description}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompetitionSchedulePage;
